/**
 * GxRail / GxRailCompact (screens §2, §1.3): Cockpit · Bank · Decide · Outlook · Results and Coach for a participant, then the round-steps guide. One element:
 * shell.css shows labels on the full rail and icons only on the compact rail (below 1360 px, on every page).
 */
import { useCoach } from "../coach/CoachDrawer";
import { Icon, type IconName } from "../theme/icons";
import { Link } from "./history";
import type { RoundGuide } from "./guide";
import { GxRoundGuide } from "./RoundGuide";

/** The quiet dot on the Outlook row: worked out once (Frame) for the year the Outlook is about, and named so ("The FY2029 outlook is not opened yet") beside a round whose own Outlook step is done. */
export interface OutlookDotInfo { readonly label: string }
export function OutlookDot({ dot }: { readonly dot: OutlookDotInfo }) {
  return <i className="gx-nav-dot" role="img" aria-label={dot.label} title={dot.label} />;
}

export interface RailItem { readonly key: string; readonly label: string; readonly icon: IconName; readonly href: string }

/** The participant's nav rows in rail order: the rail and the phone menu sheet both draw from this one list. */
export function railItems({ cohortId, active, latestReleasedRound, chairOwed = false }: { readonly cohortId: string; readonly active: string; readonly latestReleasedRound: number | null; readonly chairOwed?: boolean }): readonly RailItem[] {
  const base = `/c/${cohortId}`;
  return [
    ...(chairOwed && active !== "chair" ? [{ key: "chair", label: "Take the chair", icon: "decide" as const, href: `${base}/chair` }] : []),
    { key: "cockpit", label: "Cockpit", icon: "cockpit", href: `${base}/cockpit` },
    { key: "financials", label: "Bank", icon: "financials", href: `${base}/financials` },
    { key: "decide", label: "Decide", icon: "decide", href: `${base}/decide` },
    { key: "outlook", label: "Outlook", icon: "outlook", href: `${base}/outlook` },
    { key: "results", label: "Results", icon: "results", href: `${base}/results/${latestReleasedRound ?? 1}` },
  ];
}

/** What the Coach is told the player is looking at: Decide's review step counts as its own screen. */
export const coachScreenOf = (active: string): { readonly screen: string; readonly label: string } => ({ screen: active === "decide" && window.location.search.includes("mode=review") ? "review" : active, label: "This screen" });

export function GxRail({ cohortId, heading, active, latestReleasedRound, outlookDot, guide, chairOwed = false }: {
  readonly cohortId: string; readonly heading: string; readonly active: string;
  /** Retired (OR-30.30): the count lives in the bottom bar and the round steps; callers may still pass it. */
  readonly decideBadge?: number; readonly decideBadgeLabel?: string; readonly latestReleasedRound: number | null;
  /** The Outlook row's quiet dot (null: none to show), decided once in Frame from the year the outlook is for and whether it was opened. */
  readonly outlookDot: OutlookDotInfo | null; readonly guide: RoundGuide | null;
  /** The strategy and limits are not saved yet: "Take the chair" stays in the rail until they are. */
  readonly chairOwed?: boolean;
}) {
  const coach = useCoach();
  const items = railItems({ cohortId, active, latestReleasedRound, chairOwed });
  return (
    <nav className="gx-rail" aria-label="Main">
      <h6 className="gx-eyebrow gx-rail-h">{heading}</h6>
      {items.map((it) => (
        <Link key={it.key} href={it.href} className="gx-nav" aria-current={active === it.key ? "page" : undefined}
          {...(it.key === "results" && latestReleasedRound === null ? { "aria-disabled": true, tabIndex: -1 } : {})}>
          <Icon name={it.icon} /><span className="gx-nav-l">{it.label}</span>
          {it.key === "outlook" && active !== "outlook" && outlookDot !== null ? <OutlookDot dot={outlookDot} /> : null}
        </Link>
      ))}
      <h6 className="gx-eyebrow gx-rail-h">Support</h6>
      <button type="button" className="gx-nav" onClick={() => coach.open(coachScreenOf(active))}>
        <Icon name="coach" /><span className="gx-nav-l">Coach</span><span className="gx-badge gx-badge--neutral gx-nav-ask">Ask</span>
      </button>
      {guide === null ? null : <GxRoundGuide guide={guide} />}
    </nav>
  );
}
